/**
 * Vídeo, que não passa pelo mesmo caminho dos outros arquivos.
 *
 * Foto e documento vão para o Bunny Storage e são servidos como estão. Vídeo
 * vai para o **Bunny Stream**, que transcodifica para várias resoluções e
 * entrega por HLS — sem isso, um celular no campo baixaria os 80 MB do arquivo
 * original para assistir a trinta segundos.
 *
 * O que a Stream aceita não é o que a Storage aceita, e é uma lista fechada:
 * mandar um formato de fora dela faz o vídeo entrar na biblioteca e falhar na
 * transcodificação, deixando um registro quebrado no relatório.
 */

/**
 * Contêineres de vídeo aceitos pelo Bunny Stream.
 *
 * Copiado da especificação oficial (bunny.net/docs/stream/video-specification),
 * conferido em agosto de 2026.
 *
 * `m4v` não está na lista de vídeo da documentação — aparece na de áudio, junto
 * com `m4p`, o que é claramente engano deles: `.m4v` é contêiner de vídeo da
 * Apple. E a lista de vídeo traz `.4mv`, que não existe como formato e só pode
 * ser `.m4v` com as letras trocadas. Os dois entram: recusar um `.m4v` legítimo
 * por causa de um erro de digitação na documentação seria pior.
 */
export const BUNNY_STREAM_EXTENSIONS = [
  'mp4',
  'mkv',
  'webm',
  'mov',
  'avi',
  'vod',
  'flv',
  'wmv',
  'ts',
  'amv',
  'mpeg',
  'm4v',
  '4mv',
] as const;

const ACEITAS = new Set<string>(BUNNY_STREAM_EXTENSIONS);

export function isVideoExtension(extension: string): boolean {
  return ACEITAS.has(extension.toLowerCase());
}

/**
 * Teto de tamanho do vídeo.
 *
 * Bem acima do teto dos outros arquivos porque aqui os bytes **não passam pela
 * memória do servidor**: a requisição é encaminhada direto para o Bunny à
 * medida que chega. O que limita é a paciência de quem envia por rede de
 * cidade pequena, não a memória do processo.
 */
export const MAX_VIDEO_BYTES = 500 * 1024 * 1024;

/** Situação da transcodificação, do ponto de vista de quem espera na tela. */
export type VideoStatus = 'processando' | 'pronto' | 'falhou';

/**
 * Traduz o `status` numérico do Bunny.
 *
 * A escala deles mistura fila, transcodificação e erro:
 *
 * ```
 * 0 Created   1 Uploaded   2 Processing   3 Transcoding   4 Finished
 * 5 Error     6 UploadFailed              7 JitSegmenting 8 JitPlaylistsCreated
 * ```
 *
 * A tela só precisa saber se pode tocar, se deve esperar, ou se deu errado — e
 * **só `4` e `8` podem tocar**.
 *
 * O `3` engana, e enganou: em `Transcoding` a capa já existe e a duração já foi
 * medida, então o quadro na tela parecia pronto e abria o player — que mostrava
 * a tela de espera *dele*, em inglês. Medido no envio de um vídeo de 25s: aos 4s
 * status 2 com capa 404; aos 12s status 3 com **capa 200** e duração 25s, o
 * player ainda esperando; só aos 15s status 4 e o player pronto.
 *
 * Situação desconhecida conta como espera. Se o Bunny inventar um código novo,
 * pedir para aguardar é o erro barato; abrir um player que não toca, não.
 */
export function videoStatusFromBunny(status: number): VideoStatus {
  if (status === 4 || status === 8) return 'pronto';
  if (status === 5 || status === 6) return 'falhou';
  return 'processando';
}

/** O player embutido do Bunny — o mesmo que a plataforma serve. */
export function bunnyPlayerUrl(libraryId: string, guid: string): string {
  return `https://iframe.mediadelivery.net/embed/${libraryId}/${guid}`;
}

/**
 * A capa gerada pela transcodificação.
 *
 * O CDN da biblioteca recusa requisição **sem** cabeçalho `Referer` — é a
 * proteção de acesso direto que vem ligada por padrão. O navegador manda o
 * `Referer` sozinho ao carregar uma `<img>`, então na tela funciona; o que não
 * funciona é buscar essa URL do servidor.
 */
export function bunnyThumbnailUrl(cdnHost: string, guid: string): string {
  return `https://${cdnHost}/${guid}/thumbnail.jpg`;
}

/** `95` → `"1:35"`; `3720` → `"1:02:00"`. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || seconds <= 0) return '—';

  const horas = Math.floor(seconds / 3600);
  const minutos = Math.floor((seconds % 3600) / 60);
  const segundos = Math.floor(seconds % 60);

  const doisDigitos = (valor: number): string => String(valor).padStart(2, '0');

  return horas > 0
    ? `${horas}:${doisDigitos(minutos)}:${doisDigitos(segundos)}`
    : `${minutos}:${doisDigitos(segundos)}`;
}
